import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { RgbColorPicker } from 'react-colorful';
import ReactQuill from 'react-quill';

import {
  FormHelperText,
  Popover,
  Stack,
  TextFieldProps,
  Tooltip
} from '@mui/material';

import {
  formats,
  isRichTextEmpty,
  rgbObjectToString,
  rgbStringToObject
} from '../../utils/colorUtils';

import 'react-quill/dist/quill.snow.css';
import './style.scss';

const BLACK_COLOR = 'rgb(0, 0, 0)';
const QUILL_FORMAT_DELAY = 50;

export type AcAdvancedTextInputProps = TextFieldProps & {
  header?: string;
  tooltip?: string;
  value?: string;
  textarea?: boolean;
  helperText?: string;
  enableCopy?: boolean;
  required?: boolean;
  customClass?: string;
  enableColorPicker?: boolean;
  setValue: (value: string) => any;
  fontColor?: string;
  handleOnEmpty?: () => void;
};

export const AcAdvancedTextInput: React.FC<AcAdvancedTextInputProps> = ({
  header = '',
  tooltip = '',
  textarea = false,
  value = '',
  helperText = '',
  required = false,
  customClass = '',
  enableColorPicker = false,
  setValue,
  fontColor,
  handleOnEmpty,
  ...props
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [colorPickerAnchor, setColorPickerAnchor] =
    useState<HTMLElement | null>(null);
  const [selectedColor, setSelectedColor] = useState(BLACK_COLOR);
  const [userSelectedColor, setUserSelectedColor] = useState<string | null>(
    null
  );
  const savedRange = useRef<any>(null);
  const [wasEmpty, setWasEmpty] = useState(true);

  const supportsCustomFontColor = useMemo(() => {
    return enableColorPicker || fontColor;
  }, [fontColor, enableColorPicker]);

  // Custom text color handler
  const handleTextColor = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      // Save the current selection
      savedRange.current = quill.getSelection();
    }

    // Find the textColor button in the toolbar
    const toolbar = quillRef.current?.getEditor()?.getModule('toolbar');
    const textColorButton = toolbar?.container?.querySelector('.ql-textColor');
    if (textColorButton) {
      setColorPickerAnchor(textColorButton as HTMLElement);
    }
  }, []);

  const handleColorChange = useCallback(
    (color: { r: number; g: number; b: number }) => {
      const rgbString = rgbObjectToString(color);
      setSelectedColor(rgbString);
    },
    []
  );

  const handleCloseColorPicker = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const length = quill.getLength();
      quill.formatText(0, length - 1, 'color', selectedColor);
      setUserSelectedColor(selectedColor);
    }

    setColorPickerAnchor(null);
    savedRange.current = null;
  }, [selectedColor]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline'],
          ['link'],
          ...(enableColorPicker ? [['textColor']] : [])
        ],
        handlers: {
          ...(enableColorPicker ? { textColor: handleTextColor } : {})
        }
      }
    }),
    [enableColorPicker, handleTextColor]
  );

  const applyColorToEditor = useCallback(
    (color: string) => {
      if (!supportsCustomFontColor) return;

      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      setTimeout(() => {
        const length = quill.getLength();
        if (length > 1) {
          const textLength = length - 1;
          quill.formatText(0, textLength, 'color', color);
        }
      }, QUILL_FORMAT_DELAY);
    },
    [supportsCustomFontColor]
  );

  const handleEmptyContent = useCallback(() => {
    setSelectedColor(BLACK_COLOR);
    setUserSelectedColor(null);
    setWasEmpty(true);
    handleOnEmpty?.();
  }, []);

  const handleEmptyToContentTransition = useCallback(() => {
    setSelectedColor(BLACK_COLOR);
    setUserSelectedColor(null);
    setWasEmpty(false);
    applyColorToEditor(BLACK_COLOR);
  }, [applyColorToEditor]);

  const handleExistingContent = useCallback(
    (colorToApply: string) => {
      setSelectedColor(colorToApply);
      setWasEmpty(false);
      applyColorToEditor(colorToApply);
    },
    [applyColorToEditor]
  );

  // Apply color to text (prioritize user selection over backend)
  useEffect(() => {
    if (!supportsCustomFontColor) return;

    const isEmpty = isRichTextEmpty(value);

    if (isEmpty) {
      handleEmptyContent();
      return;
    }

    if (wasEmpty && !isEmpty) {
      handleEmptyToContentTransition();
      return;
    }

    // For existing non-empty content, use user selection or fallback to fontColor
    const colorToApply = userSelectedColor || fontColor || BLACK_COLOR;
    handleExistingContent(colorToApply);
  }, [
    supportsCustomFontColor,
    fontColor,
    value,
    userSelectedColor,
    wasEmpty,
    handleEmptyContent,
    handleEmptyToContentTransition,
    handleExistingContent
  ]);

  return (
    <Stack
      className={`input-container formContent-input ${customClass}`}
      direction="column"
    >
      <Stack
        direction="row"
        alignItems="center"
        marginBottom="8px"
        gap="5px"
        className="input-headerBlock formContent-input-fieldTitle"
      >
        {header && (
          <Tooltip arrow title={tooltip} placement="top">
            <h3>
              {required && (
                <span style={{ color: 'red', marginRight: '6px' }}>*</span>
              )}
              {header}
            </h3>
          </Tooltip>
        )}
      </Stack>
      <div className="rich-text-editor-wrapper">
        <div data-text-editor="name">
          <ReactQuill
            className={`rich-text-editor ${customClass}`}
            ref={quillRef}
            theme="snow"
            modules={modules}
            formats={formats}
            value={value}
            onChange={setValue}
            placeholder="Start typing your content..."
            bounds={`[data-text-editor="name"]`}
          />
        </div>
        {/* Color Picker Popover */}
        <Popover
          open={Boolean(colorPickerAnchor)}
          anchorEl={colorPickerAnchor}
          onClose={handleCloseColorPicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              style: {
                padding: '16px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
                maxWidth: '220px',
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                opacity: 1
              }
            }
          }}
        >
          <div className="w-[180px]">
            <RgbColorPicker
              color={rgbStringToObject(selectedColor)}
              onChange={handleColorChange}
              className="w-full h-[150px]"
            />
            <div style={{ marginTop: '12px' }}>
              <input
                type="text"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono text-center"
                placeholder={BLACK_COLOR}
              />
            </div>
          </div>
        </Popover>
      </div>
      {helperText && (
        <FormHelperText
          error={props.error}
          sx={{
            marginTop: '8px',
            fontSize: '12px',
            color: props.error ? '#d32f2f' : '#666'
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Stack>
  );
};
